import { useState } from 'react'
import { Flag, Check, X } from 'lucide-react'
import { createTicket } from '../../services/support.service'
import { formatSetCode } from '../../utils/setCode'
import styles from './ReportIssueModal.module.css'

export interface ReportSetIssueContext {
  setName: string
  setId?: string
  setCode?: string
  gameName?: string
  setType?: string
  releaseDate?: string
  actualTotal?: number | null
  printedTotal?: number | null
  source?: string
  extra?: Record<string, any>
}

interface ReportSetIssueModalProps {
  isOpen: boolean
  onClose: () => void
  context: ReportSetIssueContext
}

const REPORT_SET_ISSUES = [
  { value: 'wrong_name', label: 'Set name is incorrect' },
  { value: 'wrong_release_date', label: 'Release date is incorrect' },
  { value: 'wrong_set_type', label: 'Set type is incorrect' },
  { value: 'wrong_card_count', label: 'Card count is incorrect' },
  { value: 'missing_cards', label: 'Cards are missing from this set' },
  { value: 'wrong_image', label: 'Set image / symbol is wrong or missing' },
  { value: 'other', label: 'Other issue' },
] as const

export function ReportSetIssueModal({ isOpen, onClose, context }: ReportSetIssueModalProps) {
  const [issue, setIssue] = useState('')
  const [details, setDetails] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState<string | null>(null)

  if (!isOpen) return null

  const handleClose = () => {
    if (submitting) return
    setIssue('')
    setDetails('')
    setSuccess(false)
    setError(null)
    onClose()
  }

  const handleSubmit = async () => {
    if (!issue) return

    const issueLabel = REPORT_SET_ISSUES.find((item) => item.value === issue)?.label || issue
    const description = [
      `Issue: ${issueLabel}`,
      details ? `Details: ${details}` : null,
      '',
      '--- Set Info ---',
      `Set: ${context.setName}`,
      context.setId ? `Set ID: ${context.setId}` : null,
      context.setCode ? `Set Code: ${context.setCode}` : null,
      context.gameName ? `Game: ${context.gameName}` : null,
      context.setType ? `Set Type: ${context.setType}` : null,
      context.releaseDate ? `Release Date: ${new Date(context.releaseDate).toLocaleDateString('en-AU')}` : null,
      Number.isFinite(Number(context.actualTotal)) ? `Card Count: ${Number(context.actualTotal)}` : null,
      Number.isFinite(Number(context.printedTotal)) ? `Printed Total: ${Number(context.printedTotal)}` : null,
      context.source ? `Source Page: ${context.source}` : null,
    ].filter(Boolean).join('\n')

    setSubmitting(true)
    setError(null)

    try {
      await createTicket({
        category: 'data_issue',
        priority: issue === 'wrong_release_date' || issue === 'missing_cards' ? 'high' : 'normal',
        title: `Set Issue: ${context.setName} — ${issueLabel}`,
        description,
        context: {
          ...context.extra,
          subjectType: 'set',
          issueType: issue,
          setId: context.setId,
          setName: context.setName,
          setCode: context.setCode,
          gameName: context.gameName,
          setType: context.setType,
          releaseDate: context.releaseDate,
          actualTotal: context.actualTotal ?? null,
          printedTotal: context.printedTotal ?? null,
          source: context.source,
        },
      })
      setSuccess(true)
    } catch (err: any) {
      console.error('Failed to submit set report:', err)
      setError(err.response?.data?.message || 'Failed to submit report. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className={styles.overlay} onClick={handleClose}>
      <div className={styles.modal} onClick={(event) => event.stopPropagation()}>
        <div className={styles.header}>
          <h3><Flag size={18} /> Report Set Issue</h3>
          <button type="button" className={styles.closeBtn} onClick={handleClose}>
            <X size={20} />
          </button>
        </div>

        {success ? (
          <div className={styles.body}>
            <div className={styles.successMessage}>
              <Check size={24} />
              <div>
                <strong>Report submitted — thank you!</strong>
                <p>
                  We&apos;ll review the issue with <em>{context.setName}</em> and work on a fix.
                  You can track your report in Account → Support.
                </p>
              </div>
            </div>
            <div className={styles.actions}>
              <button type="button" className={styles.primaryBtn} onClick={handleClose}>
                Close
              </button>
            </div>
          </div>
        ) : (
          <div className={styles.body}>
            <p className={styles.subtext}>
              Report an issue with <strong>{context.setName}</strong>
              {formatSetCode(context.setCode) ? ` (${formatSetCode(context.setCode)})` : ''}
            </p>

            {error ? <div className={styles.errorMsg}>{error}</div> : null}

            <label className={styles.label}>
              What&apos;s wrong?
              <select
                value={issue}
                onChange={(event) => setIssue(event.target.value)}
                className={styles.select}
              >
                <option value="">Select an issue…</option>
                {REPORT_SET_ISSUES.map((item) => (
                  <option key={item.value} value={item.value}>{item.label}</option>
                ))}
              </select>
            </label>

            {issue ? (
              <label className={styles.label}>
                Additional details {issue !== 'other' ? '(optional)' : '(required)'}
                <textarea
                  value={details}
                  onChange={(event) => setDetails(event.target.value)}
                  className={styles.textarea}
                  rows={3}
                  placeholder={
                    issue === 'missing_cards'
                      ? 'e.g. "Cards 57-64 are missing from this set"'
                      : issue === 'other'
                        ? 'Please describe the issue…'
                        : 'Any extra context that might help us fix this…'
                  }
                />
              </label>
            ) : null}

            <div className={styles.actions}>
              <button type="button" className={styles.cancelBtn} onClick={handleClose} disabled={submitting}>
                Cancel
              </button>
              <button
                type="button"
                className={styles.primaryBtn}
                onClick={handleSubmit}
                disabled={submitting || !issue || (issue === 'other' && !details.trim())}
              >
                {submitting ? 'Submitting…' : 'Submit Report'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default ReportSetIssueModal